import React, { useEffect, useState } from 'react';
import './LandingPage.css';
import logo from '../assets/futeval-logo.png';
import backgroundImage from '../assets/images/soccer-background.jpg';
import characterImage from '../assets/images/knockout-character.png';

interface LandingPageProps {
  onGetStarted: (userType: 'coaches' | 'players' | 'parents') => void;
}

const LandingPage: React.FC<LandingPageProps> = ({ onGetStarted }) => {
  const [fromPortal, setFromPortal] = useState(false);

  useEffect(() => {
    // Check if accessed from Parent Portal
    // Portal theme ONLY applies to landing page, not other pages
    const checkPortalTheme = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const fromPortalParam = urlParams.get('fromPortal');
      const hash = window.location.hash;
      const fullUrl = window.location.href;
      
      console.log('🔍 Checking portal theme:', {
        url: fullUrl,
        search: window.location.search,
        hash: hash,
        fromPortalParam: fromPortalParam,
        sessionStorage: sessionStorage.getItem('fromPortal'),
        isIframe: window.self !== window.top
      });
      
      // Check URL parameter first
      if (fromPortalParam === 'true') {
        setFromPortal(true);
        // Store in sessionStorage to persist across navigation within iframe
        sessionStorage.setItem('fromPortal', 'true');
        console.log('✅ Portal theme activated from URL parameter');
        return;
      }
      
      // Check if we're in an iframe (likely from portal)
      if (window.self !== window.top) {
        console.log('🔍 Detected iframe context - checking for portal access');
        // Only activate portal mode if URL contains portal indicators
        if (fullUrl.includes('parentportal') || fullUrl.includes('skilz') || fullUrl.includes('localhost:3002')) {
          setFromPortal(true);
          sessionStorage.setItem('fromPortal', 'true');
          console.log('✅ Portal theme activated (iframe detected with portal URL indicators)');
          return;
        }
        // If in iframe but no portal indicators, check sessionStorage
        const stored = sessionStorage.getItem('fromPortal');
        if (stored === 'true') {
          setFromPortal(true);
          console.log('✅ Portal theme activated from sessionStorage (iframe detected)');
          return;
        }
        // If in iframe but no portal indicators and no sessionStorage, don't assume portal
        console.log('ℹ️ In iframe but no portal indicators found, not activating portal theme');
        setFromPortal(false);
        sessionStorage.removeItem('fromPortal');
        return;
      }
      
      // NOT in iframe - clear portal mode unless explicitly set
      console.log('ℹ️ Not in iframe - clearing portal mode');
      setFromPortal(false);
      sessionStorage.removeItem('fromPortal');
    };
    
    // Check immediately
    checkPortalTheme();
    
    // Also check on hash change (for SPA navigation)
    window.addEventListener('hashchange', checkPortalTheme);
    
    // Check periodically in case URL changes (for iframe navigation)
    const interval = setInterval(checkPortalTheme, 500);
    
    return () => {
      window.removeEventListener('hashchange', checkPortalTheme);
      clearInterval(interval);
    };
  }, []);

  // Force portal theme application via DOM manipulation
  useEffect(() => {
    const applyPortalTheme = () => {
      const landingPageElement = document.querySelector('.landing-page');
      if (!landingPageElement) {
        // Element not ready yet, try again
        setTimeout(applyPortalTheme, 100);
        return;
      }
      
      if (fromPortal) {
        console.log('🎨 Applying portal theme to DOM element');
        landingPageElement.classList.add('from-portal');
        landingPageElement.setAttribute('data-from-portal', 'true');
        
        // Also apply inline styles as backup
        (landingPageElement as HTMLElement).style.background = 'linear-gradient(135deg, #4a148c 0%, #6a1b9a 50%, #7b1fa2 100%)';
        (landingPageElement as HTMLElement).style.backgroundImage = 'none';
        
        console.log('✅ Portal theme applied to DOM');
        console.log('   Classes:', landingPageElement.className);
        console.log('   Data attribute:', landingPageElement.getAttribute('data-from-portal'));
      } else {
        console.log('ℹ️ Portal theme not active, fromPortal state:', fromPortal);
      }
    };
    
    // Apply immediately and also after a short delay to ensure DOM is ready
    applyPortalTheme();
    setTimeout(applyPortalTheme, 100);
    setTimeout(applyPortalTheme, 500);
  }, [fromPortal]);

  return (
    <div 
      className={`landing-page ${fromPortal ? 'from-portal' : ''}`}
      data-from-portal={fromPortal ? 'true' : 'false'}
      style={fromPortal ? {
        background: 'linear-gradient(135deg, #4a148c 0%, #6a1b9a 50%, #7b1fa2 100%)',
        backgroundImage: 'none',
      } : {
        backgroundImage: `url(${backgroundImage})`,
      }}
    >
      <div className="landing-content">
        <div className="logo-wrapper">
          <img src={logo} alt="Futeval+ Logo" className="landing-logo" />
          {fromPortal && (
            <img 
              src={characterImage} 
              alt="Character" 
              className="character-peeking"
            />
          )}
        </div>
        {!fromPortal && <h1>Futeval<span className="plus-sign">+</span></h1>}
        <p className="tagline">Comprehensive Player Performance Evaluation</p>
        <p className="description">
          Futeval+ is a cutting-edge platform that empowers coaches, players, and parents with 
          comprehensive performance analytics. Track technical skills, tactical awareness, physical 
          conditioning, and psycho-social development through detailed evaluations and AI-powered insights. 
          Make data-driven decisions to elevate player performance and unlock their full potential.
        </p>
        <div className="user-type-buttons">
          {fromPortal ? (
            // Only show Parents button when accessed from Parent Portal
            <button onClick={() => onGetStarted('parents')} className="user-type-btn">
              PARENTS
            </button>
          ) : (
            // Show all three buttons when accessed normally
            <>
              <button onClick={() => onGetStarted('coaches')} className="user-type-btn">
                COACHES
              </button>
              <button onClick={() => onGetStarted('players')} className="user-type-btn">
                PLAYERS
              </button>
              <button onClick={() => onGetStarted('parents')} className="user-type-btn">
                PARENTS
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
